import conf from 'conf/conf';

/** Довжина IBAN за ISO 13616 (код країни → загальна кількість символів). */
const IBAN_TOTAL_LENGTH: Record<string, number> = {
    AT: 20,
    BE: 16,
    CZ: 24,
    DE: 22,
    EE: 20,
    ES: 24,
    FR: 27,
    GB: 22,
    HU: 28,
    IT: 27,
    LT: 20,
    LV: 21,
    MD: 24,
    NL: 18,
    PL: 28,
    RO: 24,
    SK: 24,
    UA: 29,
};

const DEFAULT_IBAN_COUNTRY = 'UA';
const DEFAULT_IBAN_LENGTH = IBAN_TOTAL_LENGTH[DEFAULT_IBAN_COUNTRY];

export const getIbanCountry = (rawCountry?: string): string => {
    const country = String(rawCountry || conf.ibanCountry || DEFAULT_IBAN_COUNTRY)
        .trim()
        .toUpperCase();
    return /^[A-Z]{2}$/.test(country) ? country : DEFAULT_IBAN_COUNTRY;
};

export const getIbanTotalLength = (country: string): number =>
    IBAN_TOTAL_LENGTH[country] || DEFAULT_IBAN_LENGTH;

/** Маска з літералами країни, напр. UA## #### #### #### #### #### ##### */
export const buildIbanFormat = (country: string): string => {
    const code = getIbanCountry(country);
    let remaining = Math.max(getIbanTotalLength(code) - code.length, 0);
    const chunks: string[] = [];

    if (remaining >= 2) {
        chunks.push(`${code}##`);
        remaining -= 2;
    } else {
        chunks.push(code);
    }

    while (remaining > 4) {
        if (remaining === 5) break;
        chunks.push('####');
        remaining -= 4;
    }
    if (remaining > 0) {
        chunks.push('#'.repeat(remaining));
    }

    return chunks.join(' ');
};

export const normalizeIban = (iban?: string | null): string =>
    String(iban || '')
        .replace(/\s+/g, '')
        .toUpperCase();

/** Цифрова частина IBAN без коду країни — для PatternFormat. */
export const getIbanPatternValue = (iban: string | undefined, country: string): string => {
    const raw = normalizeIban(iban);
    if (!raw) return '';
    const code = getIbanCountry(country);
    if (raw.startsWith(code)) return raw.slice(code.length).replace(/\D/g, '');
    if (/^[A-Z]{2}/.test(raw)) return raw.slice(2).replace(/\D/g, '');
    return raw.replace(/\D/g, '');
};

export const composeIban = (body: string, country: string): string => {
    const digits = String(body || '').replace(/\D/g, '');
    if (!digits) return '';
    return `${getIbanCountry(country)}${digits}`;
};
